import React from 'react';
import { Link } from 'react-router-dom';
import { PageContainer, PageHeader } from '@/components/layout';
import { PageTitle } from '@/components/common/PageTitle';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckCircle, ArrowRight, Zap, Globe, Upload, Users, CreditCard, HelpCircle, Key, MessageSquare, FileText } from 'lucide-react';

export default function UserGuide() {
  return (
    <>
      <PageTitle title="이용 가이드" />
      <PageContainer maxWidth="full">
      <PageHeader 
        title="이용가이드"
        description="이미지ALT 자동 생성 서비스의 주요 기능과 사용 방법을 안내합니다."
      />

      <div className="space-y-8">
        {/* 서비스 개요 */}
        <section>
          <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <Zap className="w-5 h-5 text-blue-600" />
            서비스 개요
          </h2>
          <Card>
            <CardContent className="pt-6">
              <p className="text-gray-700 leading-relaxed">
                이미지ALT는 AI를 활용하여 웹사이트의 이미지에 자동으로 대체 텍스트(Alt Text)를 생성하는 서비스입니다.
                웹 접근성을 향상하고 SEO 최적화를 통해 검색 엔진 노출을 높일 수 있습니다.
              </p>
            </CardContent>
          </Card>
        </section>

        {/* 빠른 시작 */}
        <section>
          <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <ArrowRight className="w-5 h-5 text-green-600" />
            빠른 시작
          </h2>
          <div className="space-y-3">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-semibold text-sm">1</div>
                  <div>
                    <h3 className="font-medium text-gray-800">회원가입/로그인</h3>
                    <p className="text-sm text-gray-600 mt-1">계정을 생성하고 로그인합니다.</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-semibold text-sm">2</div>
                  <div>
                    <h3 className="font-medium text-gray-800">컨텍스트 프로필 설정</h3>
                    <p className="text-sm text-gray-600 mt-1">
                      웹사이트의 주제와 키워드를 설정하여 더 정확한 대체 텍스트를 생성합니다.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-semibold text-sm">3</div>
                  <div>
                    <h3 className="font-medium text-gray-800">이미지 생성 시작</h3>
                    <p className="text-sm text-gray-600 mt-1">
                      웹사이트 URL, 이미지 URL, 또는 직접 업로드 방식으로 이미지를 추가합니다.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-semibold text-sm">4</div>
                  <div>
                    <h3 className="font-medium text-gray-800">결과 확인 및 다운로드</h3>
                    <p className="text-sm text-gray-600 mt-1">
                      생성된 대체 텍스트를 확인하고 엑셀로 다운로드합니다.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* 주요 기능 */}
        <section>
          <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-purple-600" />
            주요 기능
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card className="hover:shadow-md transition-shadow">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Globe className="w-5 h-5 text-blue-600" />
                  웹사이트 크롤러
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-600 mb-3">
                  웹사이트 URL을 입력하면 자동으로 모든 이미지를 수집하고 대체 텍스트를 생성합니다.
                </p>
                <Link to="/dashboard/crawler/website">
                  <Button variant="outline" size="sm" className="w-full">
                    바로가기
                  </Button>
                </Link>
              </CardContent>
            </Card>
            <Card className="hover:shadow-md transition-shadow">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Upload className="w-5 h-5 text-green-600" />
                  이미지 URL 크롤러
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-600 mb-3">
                  이미지 URL 목록을 일괄 입력하여 대체 텍스트를 생성합니다.
                </p>
                <Link to="/dashboard/crawler/image-url">
                  <Button variant="outline" size="sm" className="w-full">
                    바로가기
                  </Button>
                </Link>
              </CardContent>
            </Card>
            <Card className="hover:shadow-md transition-shadow">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Upload className="w-5 h-5 text-orange-600" />
                  이미지 업로드
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-600 mb-3">
                  로컬 이미지를 직접 업로드하여 대체 텍스트를 생성합니다.
                </p>
                <Link to="/dashboard/crawler/upload">
                  <Button variant="outline" size="sm" className="w-full">
                    바로가기
                  </Button>
                </Link>
              </CardContent>
            </Card>
            <Card className="hover:shadow-md transition-shadow">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Users className="w-5 h-5 text-purple-600" />
                  컨텍스트 프로필
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-600 mb-3">
                  웹사이트 주제와 키워드를 설정하여 더 정확한 대체 텍스트를 생성합니다.
                </p>
                <Link to="/dashboard/profile/context">
                  <Button variant="outline" size="sm" className="w-full">
                    바로가기
                  </Button>
                </Link>
              </CardContent>
            </Card>
            <Card className="hover:shadow-md transition-shadow">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Key className="w-5 h-5 text-indigo-600" />
                  API 연동
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-600 mb-3">
                  API 키를 발급받아 다양한 방식으로 연동합니다.
                </p>
                <Link to="/dashboard/api-docs">
                  <Button variant="outline" size="sm" className="w-full">
                    바로가기
                  </Button>
                </Link>
              </CardContent>
            </Card>
            <Card className="hover:shadow-md transition-shadow">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Users className="w-5 h-5 text-purple-600" />
                  팀 관리
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-600 mb-3">
                  팀을 생성하고 멤버를 초대하여 협업합니다.
                </p>
                <Link to="/dashboard/profile/edit#team">
                  <Button variant="outline" size="sm" className="w-full">
                    바로가기
                  </Button>
                </Link>
              </CardContent>
            </Card>
            <Card className="hover:shadow-md transition-shadow">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <CreditCard className="w-5 h-5 text-red-600" />
                  요금제 관리
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-600 mb-3">
                  플랜을 변경하고 결제 내역을 확인합니다.
                </p>
                <Link to="/dashboard/my/plan">
                  <Button variant="outline" size="sm" className="w-full">
                    바로가기
                  </Button>
                </Link>
              </CardContent>
            </Card>
            <Card className="hover:shadow-md transition-shadow">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <FileText className="w-5 h-5 text-teal-600" />
                  증빙 설정
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-600 mb-3">
                  세금계산서 발행을 위한 사업자 정보를 설정합니다.
                </p>
                <Link to="/dashboard/profile/edit#tax">
                  <Button variant="outline" size="sm" className="w-full">
                    바로가기
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* API 연동 안내 */}
        <section>
          <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <Key className="w-5 h-5 text-indigo-600" />
            API 연동 방법
          </h2>
          <Card>
            <CardContent className="pt-6 space-y-4">
              <div>
                <h3 className="font-medium text-gray-800 mb-2">API 키 발급</h3>
                <p className="text-sm text-gray-600">
                  API 연동을 위해서는 API 키가 필요합니다. API 문서 페이지에서 키를 발급받을 수 있습니다.
                  키는 개인/팀별로 관리되며, 보안을 위해 주기적으로 재발급하는 것을 권장합니다.
                </p>
              </div>
              <div>
                <h3 className="font-medium text-gray-800 mb-2">연동 방법</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-2 px-3 font-medium text-gray-800">구분</th>
                        <th className="text-left py-2 px-3 font-medium text-gray-800">내용</th>
                        <th className="text-center py-2 px-3 font-medium text-gray-800">바로가기</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b">
                        <td className="py-2 px-3 text-gray-700">시스템 직접 연동</td>
                        <td className="py-2 px-3 text-gray-600">REST API를 통해 개발자가 직접 시스템에 연동</td>
                        <td className="py-2 px-3 text-center">
                          <Link to="/dashboard/api-docs">
                            <Button variant="outline" size="sm" className="text-xs">
                              API 문서
                            </Button>
                          </Link>
                        </td>
                      </tr>
                      <tr className="border-b">
                        <td className="py-2 px-3 text-gray-700">크롬 웹스토어</td>
                        <td className="py-2 px-3 text-gray-600">브라우저 확장 프로그램 설치로 간편 연동</td>
                        <td className="py-2 px-3 text-center">
                          <Link to="/dashboard/extension-guide">
                            <Button variant="outline" size="sm" className="text-xs">
                              가이드
                            </Button>
                          </Link>
                        </td>
                      </tr>
                      <tr className="border-b">
                        <td className="py-2 px-3 text-gray-700">JS 스니펫</td>
                        <td className="py-2 px-3 text-gray-600">웹사이트에 코드 조각 삽입으로 실시간 연동</td>
                        <td className="py-2 px-3 text-center">
                          <Link to="/dashboard/snippet-guide">
                            <Button variant="outline" size="sm" className="text-xs">
                              가이드
                            </Button>
                          </Link>
                        </td>
                      </tr>
                      <tr>
                        <td className="py-2 px-3 text-gray-700">Cafe24 ALT 생성</td>
                        <td className="py-2 px-3 text-gray-600">Cafe24 쇼핑몰 플러그인으로 자동 연동</td>
                        <td className="py-2 px-3 text-center">
                          <Link to="/dashboard/shop-extension-guide">
                            <Button variant="outline" size="sm" className="text-xs">
                              가이드
                            </Button>
                          </Link>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
              <div>
                <h3 className="font-medium text-gray-800 mb-2">사용량 확인</h3>
                <p className="text-sm text-gray-600">
                  API 이용 현황 페이지에서 API 호출 횟수와 사용량을 실시간으로 확인할 수 있습니다.
                  사용량이 한도에 도달하면 알림을 받게 됩니다.
                </p>
              </div>
              <div className="pt-2">
                <Link to="/dashboard/api-docs">
                  <Button variant="outline" size="sm" className="w-full">
                    API 문서 보기
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* FAQ */}
        <section>
          <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <HelpCircle className="w-5 h-5 text-amber-600" />
            자주 묻는 질문
          </h2>
          <div className="space-y-3">
            <Card>
              <CardContent className="pt-6">
                <h3 className="font-medium text-gray-800 mb-2">Q: 대체 텍스트는 어떻게 생성되나요?</h3>
                <p className="text-sm text-gray-600">
                  AI가 이미지의 내용을 분석하여 웹 접근성 가이드라인에 맞는 설명적인 텍스트를 자동으로 생성합니다.
                  컨텍스트 프로필을 설정하면 웹사이트의 주제에 맞는 더 정확한 텍스트를 생성할 수 있습니다.
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <h3 className="font-medium text-gray-800 mb-2">Q: 생성된 대체 텍스트를 어떻게 적용하나요?</h3>
                <p className="text-sm text-gray-600">
                  엑셀 파일로 다운로드한 후 웹사이트의 HTML 코드에 직접 적용하거나,
                  개발팀에 전달하여 적용을 요청할 수 있습니다.
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <h3 className="font-medium text-gray-800 mb-2">Q: 사용량이 초과되면 어떻게 되나요?</h3>
                <p className="text-sm text-gray-600">
                  무료 플랜의 경우 초과 시 추가 이용이 제한됩니다.
                  유료 플랜의 경우 초과 사용량이 예상되면 상위 플랜으로 업그레이드해야 합니다.
                  요금제 페이지에서 상세 내용을 확인하세요.
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <h3 className="font-medium text-gray-800 mb-2">Q: 팀을 생성하면 어떤 이점이 있나요?</h3>
                <p className="text-sm text-gray-600">
                  팀을 생성하면 멤버들과 사용량을 공유하고 협업할 수 있습니다.
                  팀 요금제를 이용하면 더 경제적인 가격으로 이용할 수 있습니다.
                </p>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* 1:1 문의 안내 */}
        <section>
          <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-blue-600" />
            궁금한 점이 있으신가요?
          </h2>
          <Card>
            <CardContent className="pt-6">
              <p className="text-sm text-gray-600 mb-4">
                이용가이드에서 찾으시는 답변이 없으시다면 1:1 문의를 이용해 주세요.
                담당자가 빠르게 답변해 드리겠습니다.
              </p>
              <Link to="/dashboard/inquiry/new">
                <Button className="w-full">
                  1:1 문의하기
                </Button>
              </Link>
            </CardContent>
          </Card>
        </section>
      </div>
    </PageContainer>
    </>
  );
}
